import {
  useCallback,
  useRef,
  useState,
  useMemo,
  useEffect,
  ReactNode,
} from 'react'
import { useShallow } from 'zustand/react/shallow'
import { useDocumentState } from '@/state'
import { useListTemplateAttributes } from '@/services/queries/template_attributes'
import type { TemplateAttribute } from '@/services/hooks/template_attributes'
import { toRenderableKonvaAnnotation } from '@/pages/DocumentPage/components/Annotations/legacyAnnotationMappers'
import {
  getPointExtents,
  getSelectionFrame,
  isLineBasedAnnotation,
} from '@/pages/DocumentPage/components/Annotations/annotationGeometry'

type SelectionBox = {
  startX: number
  startY: number
  endX: number
  endY: number
}

type SelectableTemplateBound = {
  bounds: {
    bottom: number
    left: number
    right: number
    top: number
  }
  selectionFrame: SelectionPreviewFrame
  template: TemplateAttribute<any>
  templateId: string
}

type SelectionPreviewFrame =
  | {
      mode: 'axis'
      cornerRadius: number
      height: number
      left: number
      templateId: string
      top: number
      width: number
    }
  | {
      mode: 'oriented'
      angle: number
      cornerRadius: number
      height: number
      left: number
      offsetLeft: number
      offsetTop: number
      templateId: string
      top: number
      width: number
    }

const getTemplateBoundsElement = (parentEl: HTMLDivElement) =>
  (parentEl.querySelector(
    '[data-konva-annotation-bounds-mirror]',
  ) as HTMLDivElement | null) ?? (parentEl.lastChild as HTMLDivElement | null)

const SELECTION_PREVIEW_BORDER_COLOR = '#818cf8'
const SELECTION_PREVIEW_BORDER_WIDTH = 2

const getAxisSelectionPreviewFrame = ({
  bounds,
  strokeOutset = 0,
  templateId,
}: {
  bounds: SelectableTemplateBound['bounds']
  strokeOutset?: number
  templateId: string
}): SelectionPreviewFrame => {
  return {
    mode: 'axis',
    cornerRadius: 0,
    height: Math.max(bounds.bottom - bounds.top + strokeOutset * 2, 1),
    left: bounds.left - strokeOutset,
    templateId,
    top: bounds.top - strokeOutset,
    width: Math.max(bounds.right - bounds.left + strokeOutset * 2, 1),
  }
}

const getSelectionPreviewFrame = ({
  bounds,
  template,
  templateId,
}: {
  bounds: SelectableTemplateBound['bounds']
  template: TemplateAttribute<any>
  templateId: string
}): SelectionPreviewFrame => {
  if (template.data_type !== 'annotation') {
    return getAxisSelectionPreviewFrame({ bounds, templateId })
  }

  const annotation = toRenderableKonvaAnnotation(template.template_values)
  const strokeWidth = annotation.style.strokeWidth ?? 2

  if (!isLineBasedAnnotation(annotation)) {
    return getAxisSelectionPreviewFrame({
      bounds,
      strokeOutset: strokeWidth / 2,
      templateId,
    })
  }

  const pointExtents = getPointExtents({
    points: annotation.geometry.points,
  })
  const axisPadding = Math.max(strokeWidth * 2, 12)
  const selectionFrame = getSelectionFrame({
    annotation,
    strokeWidth,
    visualRect: {
      height: pointExtents.height,
      width: pointExtents.width,
      x: pointExtents.minX,
      y: pointExtents.minY,
    },
  })

  if (selectionFrame.mode !== 'oriented') {
    return getAxisSelectionPreviewFrame({
      bounds,
      strokeOutset: strokeWidth / 2,
      templateId,
    })
  }

  // The cached DOM mirror bounds include hit-area padding; convert back to the
  // annotation's local point origin before applying the shared local frame.
  const annotationLeft = bounds.left - pointExtents.minX + axisPadding
  const annotationTop = bounds.top - pointExtents.minY + axisPadding

  return {
    mode: 'oriented',
    angle: selectionFrame.angle,
    cornerRadius: 0,
    height: selectionFrame.rect.height,
    left: annotationLeft + selectionFrame.origin.x,
    offsetLeft: selectionFrame.rect.x,
    offsetTop: selectionFrame.rect.y,
    templateId,
    top: annotationTop + selectionFrame.origin.y,
    width: selectionFrame.rect.width,
  }
}

type Point = { x: number; y: number }

const doRectanglesOverlap = (
  a: SelectableTemplateBound['bounds'],
  b: SelectableTemplateBound['bounds'],
) =>
  !(
    a.left > b.right ||
    a.right < b.left ||
    a.top > b.bottom ||
    a.bottom < b.top
  )

const getSelectionBoxBounds = ({
  selBottom,
  selLeft,
  selRight,
  selTop,
}: {
  selBottom: number
  selLeft: number
  selRight: number
  selTop: number
}): SelectableTemplateBound['bounds'] => ({
  bottom: selBottom,
  left: selLeft,
  right: selRight,
  top: selTop,
})

const getSelectionBoxPolygon = (
  bounds: SelectableTemplateBound['bounds'],
): Point[] => [
  { x: bounds.left, y: bounds.top },
  { x: bounds.right, y: bounds.top },
  { x: bounds.right, y: bounds.bottom },
  { x: bounds.left, y: bounds.bottom },
]

const getOrientedFramePolygon = (
  frame: Extract<SelectionPreviewFrame, { mode: 'oriented' }>,
): Point[] => {
  const radians = (frame.angle * Math.PI) / 180
  const cos = Math.cos(radians)
  const sin = Math.sin(radians)
  const transformPoint = (x: number, y: number): Point => ({
    x: frame.left + x * cos - y * sin,
    y: frame.top + x * sin + y * cos,
  })

  return [
    transformPoint(frame.offsetLeft, frame.offsetTop),
    transformPoint(frame.offsetLeft + frame.width, frame.offsetTop),
    transformPoint(
      frame.offsetLeft + frame.width,
      frame.offsetTop + frame.height,
    ),
    transformPoint(frame.offsetLeft, frame.offsetTop + frame.height),
  ]
}

const getProjection = (polygon: Point[], axis: Point) => {
  const projections = polygon.map(
    (point) => point.x * axis.x + point.y * axis.y,
  )

  return {
    max: Math.max(...projections),
    min: Math.min(...projections),
  }
}

const getPolygonAxes = (polygon: Point[]) =>
  polygon.map((point, index) => {
    const nextPoint = polygon[(index + 1) % polygon.length]
    const edge = {
      x: nextPoint.x - point.x,
      y: nextPoint.y - point.y,
    }
    const length = Math.hypot(edge.x, edge.y)

    if (!length) return { x: 0, y: 0 }

    return {
      x: -edge.y / length,
      y: edge.x / length,
    }
  })

const doPolygonsOverlap = (a: Point[], b: Point[]) => {
  const axes = [...getPolygonAxes(a), ...getPolygonAxes(b)]

  return axes.every((axis) => {
    if (!axis.x && !axis.y) return true

    const projectionA = getProjection(a, axis)
    const projectionB = getProjection(b, axis)

    return (
      projectionA.max >= projectionB.min && projectionB.max >= projectionA.min
    )
  })
}

const doesSelectionBoxOverlapFrame = ({
  selectionBoxBounds,
  selectionFrame,
  templateBounds,
}: {
  selectionBoxBounds: SelectableTemplateBound['bounds']
  selectionFrame: SelectionPreviewFrame
  templateBounds: SelectableTemplateBound['bounds']
}) => {
  if (selectionFrame.mode === 'axis') {
    return doRectanglesOverlap(templateBounds, selectionBoxBounds)
  }

  return doPolygonsOverlap(
    getSelectionBoxPolygon(selectionBoxBounds),
    getOrientedFramePolygon(selectionFrame),
  )
}

export const AnnotationsMultiSelectContainer = ({
  children,
  isReadOnly,
}: {
  children: ReactNode
  isReadOnly?: boolean
}) => {
  const currentPageInView = useDocumentState((state) => state.currentPageInView)
  const selectedElement = useDocumentState((state) => state.selectedElement)
  const { data: templateAttributes } = useListTemplateAttributes({
    documentPageId: currentPageInView,
  })
  const croppingImageId = useDocumentState((state) => state.croppingImageId)

  const containerRef = useRef<HTMLDivElement>(null)
  const containerRectRef = useRef<DOMRect | null>(null)
  const selectionFrameRef = useRef<number | null>(null)
  const pendingSelectionBoxRef = useRef<SelectionBox | null>(null)
  const selectionStartRef = useRef({ x: 0, y: 0 })
  const selectedTemplateIdsRef = useRef<string[]>([])
  const selectedTemplatesRef = useRef<TemplateAttribute<any>[]>([])
  const selectableTemplateBoundsRef = useRef<SelectableTemplateBound[]>([])
  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [selectionBox, setSelectionBox] = useState({
    startX: 0,
    startY: 0,
    endX: 0,
    endY: 0,
  })
  const [selectionPreviewFrames, setSelectionPreviewFrames] = useState<
    SelectionPreviewFrame[]
  >([])
  const getSelectableElementRef = useDocumentState(
    useShallow((state) => state.getSelectableElementRef),
  )
  const zoom = useDocumentState((state) => state.zoom)
  const zoomPercentage = useMemo(() => zoom / 100, [zoom])
  const [isSelecting, setIsSelecting] = useState(false)

  const setDocumentState = useDocumentState(
    useShallow((state) => state.setState),
  )

  const setMultiSelectDelta = useDocumentState(
    useShallow((state) => state.setMultiSelectDelta),
  )

  const startX = selectionBox.startX / zoomPercentage
  const endX = selectionBox.endX / zoomPercentage
  const startY = selectionBox.startY / zoomPercentage
  const endY = selectionBox.endY / zoomPercentage

  const selectionStyle = {
    left: Math.min(startX, endX),
    top: Math.min(startY, endY),
    width: Math.abs(endX - startX),
    height: Math.abs(endY - startY),
  }

  const cacheSelectableTemplateBounds = useCallback(() => {
    if (!Array.isArray(templateAttributes) || !containerRef.current) {
      return
    }

    const containerRect = containerRef.current.getBoundingClientRect()
    containerRectRef.current = containerRect

    selectableTemplateBoundsRef.current = templateAttributes.flatMap((tmpl) => {
      if (!tmpl.id) return []

      const parentEl = getSelectableElementRef(tmpl.id)
      if (!parentEl) return []

      const templateEl = getTemplateBoundsElement(parentEl)
      if (!templateEl) return []

      const tmplRect = templateEl.getBoundingClientRect()

      // Convert to container-relative coords, then normalize by zoom
      const tmplLeft = (tmplRect.left - containerRect.left) / zoomPercentage
      const tmplTop = (tmplRect.top - containerRect.top) / zoomPercentage
      const tmplRight = tmplLeft + tmplRect.width / zoomPercentage
      const tmplBottom = tmplTop + tmplRect.height / zoomPercentage
      const bounds = {
        bottom: tmplBottom,
        left: tmplLeft,
        right: tmplRight,
        top: tmplTop,
      }
      const templateId = tmpl.id as string

      return [
        {
          bounds,
          selectionFrame: getSelectionPreviewFrame({
            bounds,
            template: tmpl,
            templateId,
          }),
          template: tmpl,
          templateId,
        },
      ]
    })
  }, [getSelectableElementRef, templateAttributes, zoomPercentage])

  const updateSelectedTemplatesFromBox = useCallback(
    (box: SelectionBox) => {
      if (isReadOnly) return false

      // Selection box normalized by zoom to match cached annotation bounds.
      const normalizedStartX = box.startX / zoomPercentage
      const normalizedEndX = box.endX / zoomPercentage
      const normalizedStartY = box.startY / zoomPercentage
      const normalizedEndY = box.endY / zoomPercentage

      const selLeft = Math.min(normalizedStartX, normalizedEndX)
      const selTop = Math.min(normalizedStartY, normalizedEndY)
      const selRight = Math.max(normalizedStartX, normalizedEndX)
      const selBottom = Math.max(normalizedStartY, normalizedEndY)
      const selectionBoxBounds = getSelectionBoxBounds({
        selBottom,
        selLeft,
        selRight,
        selTop,
      })

      const inBoundTemplateAttributes =
        selectableTemplateBoundsRef.current.flatMap(
          ({ bounds, selectionFrame, template, templateId }) => {
            const overlaps = doesSelectionBoxOverlapFrame({
              selectionBoxBounds,
              selectionFrame,
              templateBounds: bounds,
            })

            return overlaps
              ? [{ bounds, selectionFrame, template, templateId }]
              : []
          },
        )

      const nextTemplateIds = inBoundTemplateAttributes.map(
        ({ templateId }) => templateId,
      )
      const previousTemplateIds = selectedTemplateIdsRef.current
      const didSelectionChange =
        nextTemplateIds.length !== previousTemplateIds.length ||
        nextTemplateIds.some(
          (templateId, index) => templateId !== previousTemplateIds[index],
        )

      if (!didSelectionChange) return false

      selectedTemplateIdsRef.current = nextTemplateIds
      selectedTemplatesRef.current = inBoundTemplateAttributes.map(
        ({ template }) => template,
      )
      setSelectionPreviewFrames(
        inBoundTemplateAttributes.map(({ selectionFrame }) => selectionFrame),
      )
      return true
    },
    [isReadOnly, zoomPercentage],
  )

  useEffect(() => {
    if (isReadOnly || !isSelecting) {
      return
    }

    updateSelectedTemplatesFromBox(selectionBox)
  }, [isSelecting, isReadOnly, selectionBox, updateSelectedTemplatesFromBox])

  const scheduleSelectionBoxUpdate = useCallback((box: SelectionBox) => {
    pendingSelectionBoxRef.current = box

    if (selectionFrameRef.current !== null) return

    selectionFrameRef.current = requestAnimationFrame(() => {
      selectionFrameRef.current = null
      const pendingSelectionBox = pendingSelectionBoxRef.current
      pendingSelectionBoxRef.current = null

      if (pendingSelectionBox) {
        setSelectionBox(pendingSelectionBox)
      }
    })
  }, [])

  const updateSelectionBoxFromPointer = useCallback(
    (event: { clientX: number; clientY: number }) => {
      if (isReadOnly || !containerRef.current || !isSelecting) return

      const rect =
        containerRectRef.current ?? containerRef.current.getBoundingClientRect()

      const x = event.clientX - rect.left
      const y = event.clientY - rect.top

      scheduleSelectionBoxUpdate({
        startX: selectionStartRef.current.x,
        startY: selectionStartRef.current.y,
        endX: x,
        endY: y,
      })
    },
    [isReadOnly, isSelecting, scheduleSelectionBoxUpdate],
  )

  const cancelHoldTimer = useCallback(() => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current)
      holdTimerRef.current = null
    }
  }, [])

  const finishSelection = useCallback(() => {
    if (isReadOnly) return

    cancelHoldTimer()
    const pendingSelectionBox = pendingSelectionBoxRef.current
    if (pendingSelectionBox && isSelecting) {
      updateSelectedTemplatesFromBox(pendingSelectionBox)
    }
    if (isSelecting) {
      setDocumentState({
        bulkSelectedTemplateAttributes: selectedTemplatesRef.current,
      })
    }
    setSelectionPreviewFrames([])
    pendingSelectionBoxRef.current = null
    if (selectionFrameRef.current !== null) {
      cancelAnimationFrame(selectionFrameRef.current)
      selectionFrameRef.current = null
    }
    setIsSelecting(false)
  }, [
    cancelHoldTimer,
    isReadOnly,
    isSelecting,
    setDocumentState,
    updateSelectedTemplatesFromBox,
  ])

  useEffect(() => {
    return () => {
      if (selectionFrameRef.current !== null) {
        cancelAnimationFrame(selectionFrameRef.current)
        selectionFrameRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (isReadOnly || !Array.isArray(templateAttributes) || !isSelecting) {
      return
    }

    cacheSelectableTemplateBounds()
  }, [
    cacheSelectableTemplateBounds,
    isReadOnly,
    isSelecting,
    templateAttributes,
  ])

  useEffect(() => {
    if (!isSelecting) {
      return
    }

    return () => {
      selectableTemplateBoundsRef.current = []
    }
  }, [isSelecting])

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (holdTimerRef.current) {
        clearTimeout(holdTimerRef.current)
        holdTimerRef.current = null
      }
    }
  }, [])

  if (isReadOnly) {
    return children
  }

  return (
    <div
      data-testid="annotations-multi-select-container"
      className="relative w-full h-full"
      ref={containerRef}
      onMouseDown={(e) => {
        if (isReadOnly || !containerRef.current || !!croppingImageId) return
        const target = e.target as HTMLElement | null
        if (
          document.querySelector('[role="dialog"]') ||
          target?.closest('[data-radix-popper-content-wrapper]')
        ) {
          /**
           * Invariant: portal interactions (popover/dropdown/dialog) should not
           * start area selection on the document canvas.
           */
          return
        }
        if (selectedElement?.id) {
          const parentEl = getSelectableElementRef(selectedElement.id)
          if (parentEl && parentEl.contains(e.target as Node)) {
            return
          }
        }

        setDocumentState({
          bulkSelectedTemplateAttributes: [],
        })
        selectedTemplateIdsRef.current = []
        selectedTemplatesRef.current = []
        setSelectionPreviewFrames([])

        const rect = containerRef.current.getBoundingClientRect()
        containerRectRef.current = rect
        const x = e.clientX - rect.left
        const y = e.clientY - rect.top
        selectionStartRef.current = { x, y }

        setSelectionBox({ startX: x, startY: y, endX: x, endY: y })
        setMultiSelectDelta({ x: 0, y: 0 })

        const HOLD_MS = 200

        cancelHoldTimer()
        holdTimerRef.current = setTimeout(() => {
          cacheSelectableTemplateBounds()
          setIsSelecting(true)
        }, HOLD_MS)
      }}
      onMouseUp={finishSelection}
      onMouseMove={(e) => {
        updateSelectionBoxFromPointer(e)
      }}
    >
      {children}
      {!isReadOnly && isSelecting && (
        <>
          <div
            className="absolute inset-0 z-[99] cursor-crosshair"
            onMouseDown={(event) => {
              event.stopPropagation()
            }}
            onMouseMove={(event) => {
              event.stopPropagation()
              updateSelectionBoxFromPointer(event)
            }}
            onMouseUp={(event) => {
              event.stopPropagation()
              finishSelection()
            }}
          />
          {selectionPreviewFrames.map((frame) =>
            frame.mode === 'axis' ? (
              <div
                key={`selection-preview--${frame.templateId}`}
                className="pointer-events-none absolute z-[100] box-content border"
                style={{
                  borderColor: SELECTION_PREVIEW_BORDER_COLOR,
                  borderRadius: frame.cornerRadius,
                  borderWidth: SELECTION_PREVIEW_BORDER_WIDTH,
                  height: frame.height,
                  left: frame.left - SELECTION_PREVIEW_BORDER_WIDTH,
                  top: frame.top - SELECTION_PREVIEW_BORDER_WIDTH,
                  width: frame.width,
                }}
              />
            ) : (
              <div
                key={`selection-preview--${frame.templateId}`}
                className="pointer-events-none absolute z-[100] box-content border"
                style={{
                  borderColor: SELECTION_PREVIEW_BORDER_COLOR,
                  borderRadius: frame.cornerRadius,
                  borderWidth: SELECTION_PREVIEW_BORDER_WIDTH,
                  height: frame.height,
                  left: frame.left,
                  top: frame.top,
                  transform: `rotate(${frame.angle}deg) translate(${frame.offsetLeft - SELECTION_PREVIEW_BORDER_WIDTH}px, ${frame.offsetTop - SELECTION_PREVIEW_BORDER_WIDTH}px)`,
                  transformOrigin: '0 0',
                  width: frame.width,
                }}
              />
            ),
          )}
          <div
            className="absolute border-2 border-blue-500 bg-blue-100 bg-opacity-20 pointer-events-none z-[101]"
            style={{
              left: selectionStyle.left,
              top: selectionStyle.top,
              width: selectionStyle.width,
              height: selectionStyle.height,
            }}
          />
        </>
      )}
    </div>
  )
}
